import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslations } from '../../localization/useTranslations';

interface ArticleDialogContentProps {
  title?: string;
  link?: string;
  imageLink?: string;
  onClose: () => void;
}

export default function ArticleDialogContent({ title, link, imageLink, onClose }: ArticleDialogContentProps) {
  const translations = useTranslations();
  const [showLinkCopied, setShowLinkCopied] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const onCopyToClipboardClick = () => {
    if (!link) return;

    if (navigator.clipboard) {
      navigator.clipboard.writeText(link);
    }

    setShowLinkCopied(true);
  };

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <dialog className="article-share-modal modal modal--small" open ref={dialogRef}>
      <div className="article-share-modal__wrapper modal__wrapper">
        <div className="article-share-modal__header modal__header">
          <h5>{translations?.shareModal?.shareArticle || 'Share Article'}</h5>
          <button className="close-button" onClick={onClose}></button>
        </div>
        <div className="article-share-modal__content modal__content">
          <div className="article-share-modal__meta">
            <img src={imageLink} alt="Article Preview" />
            {title && <p className="basic-16" dangerouslySetInnerHTML={{ __html: title }} />}
          </div>
          <div className="article-share-modal__social-links">
            <a
              href={`http://www.facebook.com/sharer.php?u=${link}`}
              target="_blank"
              rel="noreferrer noopener"
              className="link link--icon link-underline-16 article-share-link article-share-link--facebook"
            >
              {translations?.shareModal?.facebook || 'Facebook'}
            </a>
            <a
              href={`http://twitter.com/share?url=${link}`}
              target="_blank"
              rel="noreferrer noopener"
              className="link link--icon link-underline-16 article-share-link  article-share-link--twitter"
            >
              {translations?.shareModal?.twitter || 'Twitter'}
            </a>
            <a
              href={`http://www.linkedin.com/shareArticle?mini=true&url=${link}`}
              target="_blank"
              rel="noreferrer noopener"
              className="link link--icon link-underline-16 article-share-link  article-share-link--linkedin"
            >
              {translations?.shareModal?.linkedin || 'LinkedIn'}
            </a>

            <div className="copy-url__wrapper" onClick={onCopyToClipboardClick}>
              <div className="copy-url__confirmation-wrapper">
                <span className="link link--icon link-underline-16 article-share-link  article-share-link--copy-link">
                  {translations?.shareModal?.copyLink || 'Copy Link'}
                </span>
                {showLinkCopied && (
                  <span className="link-copied-successfully basic-16">
                    {translations?.shareModal?.copiedLink || 'Link copied'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </dialog>
  );
}
